import { BaseControl } from '.';

/**
 * @typedef {object} IconButtonOption
 * @property {string} value - Value of the option
 * @property {React.ReactNode} icon - Icon/visual representation
 * @property {string|null} [tooltip] - Tooltip text
 */

/**
 * @typedef {object} IconButtonGroupControlOptions
 * @property {IconButtonOption[]} buttons - Available icon button options
 * @property {string} [className] - Extra class on the control wrapper, for styling variants (e.g. the icon picker grid)
 */

/**
 * @extends {BaseControl<string, IconButtonGroupControlOptions>}
 */
export class IconButtonGroupControl extends BaseControl {

    /** @type {BaseControl<string>['render']} */
    render(value, onChange) {
        return (
            <div className={`icon-button-group-control ${this.options?.className ?? ''}`.trim()}>
                <label title={this.description}>
                    {this.label}
                </label>
                
                <div className="icon-button-group">
                    {this.options?.buttons?.map(option => (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => onChange(option.value)}
                            title={option.tooltip ?? ''}
                            className={`icon-button ${value === option.value ? 'active' : ''}`}
                        >
                            {option.icon}
                        </button>
                    ))}
                </div>
                {/* {this.description && (
                    <div style={{ 
                        fontSize: '0.75rem', 
                        color: '#666',
                        marginTop: '4px',
                    }}>
                        {this.description}
                    </div>
                )} */}
            </div>
        );
    }
}

/* Flex Direction Icons */
export const FlexRowIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" 
        fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M5 12h14M15 8l4 4-4 4" strokeLinecap="round" 
            strokeLinejoin="round" />
    </svg>
);

export const FlexRowReverseIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" 
        fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M19 12H5M9 8l-4 4 4 4" strokeLinecap="round" 
            strokeLinejoin="round" />
    </svg>
);

export const FlexColumnIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" 
        fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 5v14M8 15l4 4 4-4" strokeLinecap="round" 
            strokeLinejoin="round" />
    </svg>
);

export const FlexColumnReverseIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" 
        fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 19V5M8 9l4-4 4 4" strokeLinecap="round" 
            strokeLinejoin="round" />
    </svg>
);

/* Justify Content Icons */
export const JustifyStartIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" 
        fill="currentColor">
        <rect x="2" y="2" width="2" height="20" opacity="0.3" />
        <rect x="6" y="8" width="4" height="8" rx="1" />
        <rect x="12" y="8" width="4" height="8" rx="1" />
    </svg>
);

export const JustifyCenterIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" 
        fill="currentColor">
        <rect x="11" y="2" width="2" height="20" opacity="0.3" />
        <rect x="4" y="8" width="4" height="8" rx="1" />
        <rect x="16" y="8" width="4" height="8" rx="1" />
    </svg>
);

export const JustifyEndIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" 
        fill="currentColor">
        <rect x="20" y="2" width="2" height="20" opacity="0.3" />
        <rect x="8" y="8" width="4" height="8" rx="1" />
        <rect x="14" y="8" width="4" height="8" rx="1" />
    </svg>
);

export const JustifySpaceBetweenIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" 
        fill="currentColor">
        <rect x="2" y="2" width="2" height="20" opacity="0.3" />
        <rect x="20" y="2" width="2" height="20" opacity="0.3" />
        <rect x="4" y="8" width="4" height="8" rx="1" />
        <rect x="16" y="8" width="4" height="8" rx="1" />
    </svg>
);

export const JustifySpaceAroundIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" 
        fill="currentColor">
        <rect x="2" y="2" width="2" height="20" opacity="0.3" />
        <rect x="20" y="2" width="2" height="20" opacity="0.3" />
        <rect x="6" y="8" width="4" height="8" rx="1" />
        <rect x="14" y="8" width="4" height="8" rx="1" />
    </svg>
);

/* Align Items Icons */
export const AlignStartIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" 
        fill="currentColor">
        <rect x="2" y="2" width="20" height="2" opacity="0.3" />
        <rect x="6" y="6" width="4" height="6" rx="1" />
        <rect x="14" y="6" width="4" height="10" rx="1" />
    </svg>
);

export const AlignCenterIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" 
        fill="currentColor">
        <rect x="2" y="11" width="20" height="2" opacity="0.3" />
        <rect x="6" y="9" width="4" height="6" rx="1" />
        <rect x="14" y="7" width="4" height="10" rx="1" />
    </svg>
);

export const AlignEndIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" 
        fill="currentColor">
        <rect x="2" y="20" width="20" height="2" opacity="0.3" />
        <rect x="6" y="12" width="4" height="6" rx="1" />
        <rect x="14" y="8" width="4" height="10" rx="1" />
    </svg>
);

export const AlignStretchIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" 
        fill="currentColor">
        <rect x="2" y="2" width="20" height="2" opacity="0.3" />
        <rect x="2" y="20" width="20" height="2" opacity="0.3" />
        <rect x="6" y="6" width="4" height="12" rx="1" />
        <rect x="14" y="6" width="4" height="12" rx="1" />
    </svg>
);
